// src/products/capRaise/CapRaiseApp.js
import React, { useEffect } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { Home, Building, Users, BarChart3, FileText, HelpCircle } from 'lucide-react';
import { useGlobalStore } from '../../state/globalStore';
import { useCapRaiseStore } from './state/capRaiseStore';

// Import Pages
import DashboardPage from './pages/DashboardPage';
import NewRaisePage from './pages/NewRaisePage';
import ScenariosPage from './pages/ScenariosPage';
import InvestorCrmPage from './pages/InvestorCrmPage';
import BookBuildPage from './pages/BookBuildPage';
import DealDocsPage from './pages/DealDocsPage';
import TermsheetPage from './pages/TermsheetPage';
import QuestionsIntegratePage from './pages/QuestionsIntegratePage';

// Import Components
import Sidebar from '../../components/layout/Sidebar';
import SharedHeader from '../../components/layout/SharedHeader';

const CapRaiseApp = () => {
  const navigate = useNavigate();

  // Global state
  const { user, userProfile, loading: userLoading, initUser } = useGlobalStore();

  // Module state
  const {
    companies,
    selectedCompany,
    activeRound,
    loading: capRaiseLoading,
    messages,
    userRole,
    canCreate,
    addMessage,
    checkCanCreate,
    checkRoundRole,
    loadCompanies,
    loadRounds,
    handleRoundCreated,
    handleCompanyChange,
    handleRoundChange,
  } = useCapRaiseStore();

  const loading = userLoading || capRaiseLoading;

  // Initial user load
  useEffect(() => {
    initUser();
  }, [initUser]);

  // Load initial data when user is available
  useEffect(() => {
    if (user?.id) {
      checkCanCreate();
      loadCompanies();
    }
  }, [user?.id, checkCanCreate, loadCompanies]);

  // Load rounds when company changes
  useEffect(() => {
    if (selectedCompany) {
      loadRounds();
    }
  }, [selectedCompany, loadRounds]);

  // Check user's role when the active round changes
  useEffect(() => {
    if (activeRound?.id) {
      checkRoundRole(activeRound.id);
    }
  }, [activeRound, checkRoundRole]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Navigation items for CapRaise
  const navItems = [
    { id: '/capraise', icon: Home, label: 'Dashboard' },
    { id: '/capraise/new-raise', icon: Building, label: 'New Raise' },
    { id: '/capraise/scenarios', icon: BarChart3, label: 'Scenarios' },
    { id: '/capraise/investors', icon: Users, label: 'Investors' },
    { id: '/capraise/bookbuild', icon: BarChart3, label: 'BookBuild' },
    { id: '/capraise/dealdocs', icon: FileText, label: 'DealDocs' },
    { id: '/capraise/termsheet', icon: FileText, label: 'Termsheet' },
    { id: '/capraise/questions', icon: HelpCircle, label: 'Questions' }
  ];

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar
        items={navItems}
        activeItem={window.location.pathname}
        onItemClick={(itemId) => navigate(itemId)}
        productName="CapRaise"
        user={user}
        userProfile={userProfile}
        onBackToProducts={() => navigate('/my-account/dashboard')}
      />

      <div className="flex-1 flex flex-col overflow-hidden">
        <SharedHeader
          user={user}
          userProfile={userProfile}
          userRole={userRole}
          productName="CapRaise"
          selectedCompany={selectedCompany}
          companies={companies}
          setSelectedCompany={handleCompanyChange}
        />

        {messages.length > 0 && (
          <div className="fixed top-20 right-4 z-50 space-y-2">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`p-4 rounded-lg shadow-lg ${
                  message.type === 'error'
                    ? 'bg-red-50 border-l-4 border-red-400 text-red-700'
                    : message.type === 'success'
                    ? 'bg-green-50 border-l-4 border-green-400 text-green-700'
                    : 'bg-blue-50 border-l-4 border-blue-400 text-blue-700'
                }`}
              >
                <p className="text-sm font-medium">{message.text}</p>
              </div>
            ))}
          </div>
        )}

        <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-50 p-6">
          <Routes>
            <Route
              path="/"
              element={
                <DashboardPage
                  selectedCompany={selectedCompany}
                  activeRound={activeRound}
                  user={user}
                  userRole={userRole}
                  canCreate={canCreate}
                  addMessage={addMessage}
                  onCompanyChange={handleCompanyChange}
                  onRoundChange={handleRoundChange}
                />
              }
            />
            <Route
              path="/new-raise"
              element={
                <NewRaisePage
                  selectedCompany={selectedCompany}
                  activeRound={activeRound}
                  user={user}
                  userRole={userRole}
                  canCreate={canCreate}
                  addMessage={addMessage}
                  onRoundChange={handleRoundCreated}
                />
              }
            />
            <Route
              path="/scenarios"
              element={
                <ScenariosPage
                  selectedCompany={selectedCompany}
                  activeRound={activeRound}
                  user={user}
                  userRole={userRole}
                  canCreate={canCreate}
                  addMessage={addMessage}
                />
              }
            />
            <Route
              path="/investors"
              element={
                <InvestorCrmPage
                  selectedCompany={selectedCompany}
                  activeRound={activeRound}
                  user={user}
                  userRole={userRole}
                  canCreate={canCreate}
                  addMessage={addMessage}
                />
              }
            />
            <Route
              path="/bookbuild"
              element={
                <BookBuildPage
                  selectedCompany={selectedCompany}
                  activeRound={activeRound}
                  user={user}
                  userRole={userRole}
                  canCreate={canCreate}
                  addMessage={addMessage}
                />
              }
            />
            <Route
              path="/dealdocs"
              element={
                <DealDocsPage
                  selectedCompany={selectedCompany}
                  activeRound={activeRound}
                  user={user}
                  userRole={userRole}
                  canCreate={canCreate}
                  addMessage={addMessage}
                />
              }
            />
            <Route
              path="/termsheet"
              element={
                <TermsheetPage
                  selectedCompany={selectedCompany}
                  activeRound={activeRound}
                  user={user}
                  userRole={userRole}
                  canCreate={canCreate}
                  addMessage={addMessage}
                />
              }
            />
            <Route
              path="/questions"
              element={
                <QuestionsIntegratePage
                  selectedCompany={selectedCompany}
                  activeRound={activeRound}
                  user={user}
                  userRole={userRole}
                  canCreate={canCreate}
                  addMessage={addMessage}
                />
              }
            />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default CapRaiseApp;
